import type { NormalizedApiError } from '../core/normalizedApiError';

/**
 * FastAPI `HTTPException` / validation error bodies after normalization.
 * Handles: `detail` string | array | { message, error_code }`, top-level `message`.
 */
export function parseFastApiDetail(data: unknown, httpStatus?: number): NormalizedApiError | null {
  if (data === null || data === undefined) return null;
  if (typeof data === 'string') {
    return { message: sanitize(data, httpStatus), httpStatus, raw: data };
  }
  if (typeof data !== 'object') {
    return { message: 'Something went wrong. Please try again.', httpStatus: httpStatus };
  }

  const d = data as Record<string, unknown>;
  const detail = d.detail;

  if (typeof detail === 'string') {
    return {
      message: sanitize(detail, httpStatus),
      httpStatus,
      raw: data,
    };
  }

  if (Array.isArray(detail)) {
    const parts = detail.map((item: unknown) => {
      if (item && typeof item === 'object' && 'msg' in (item as object)) {
        return String((item as { msg?: string }).msg ?? item);
      }
      return String(item);
    });
    const joined = parts.filter(Boolean).join('. ') || 'Invalid input.';
    return {
      message: joined,
      errorCode: 'validation_error',
      httpStatus,
      raw: data,
    };
  }

  if (detail && typeof detail === 'object') {
    const o = detail as Record<string, unknown>;
    const msg = typeof o.message === 'string' ? o.message : '';
    const code = typeof o.error_code === 'string' ? o.error_code : undefined;
    const title = typeof o.title === 'string' ? o.title : undefined;
    if (msg) {
      return {
        message: sanitize(msg, httpStatus),
        errorCode: code,
        title,
        httpStatus,
        raw: data,
      };
    }
  }

  if (typeof d.message === 'string') {
    const code = typeof d.error_code === 'string' ? d.error_code : undefined;
    return {
      message: sanitize(d.message, httpStatus),
      errorCode: code,
      httpStatus,
      raw: data,
    };
  }

  return null;
}

function sanitize(raw: string, httpStatus?: number): string {
  const s = raw.trim();
  if (!s) return 'Something went wrong. Please try again.';
  if (httpStatus && httpStatus >= 500) {
    if (s.length > 240 || /traceback|internal server|sqlalchemy/i.test(s)) {
      return 'Something went wrong. Please try again.';
    }
  }
  return s;
}
